import { FC, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography
} from "@mui/material";
import { utils } from "@vilocnv/allsetra-core";
import { useTranslation } from "react-i18next";
import { KeyHistoryEntryDto } from "domain/keys/entities/dtos/key-history.dto";
import KeyHistoryStatusBadge from "../KeyHistoryStatusBadge";

export interface KeyHistoryTableProps {
  historyData: KeyHistoryEntryDto[];
  loading?: boolean;
}

const KeyHistoryTable: FC<KeyHistoryTableProps> = ({
  historyData,
  loading = false
}) => {
  const { t } = useTranslation(["translation"]);

  // Sort from most recent to oldest
  const sortedHistoryData = useMemo(() => {
    return [...historyData].sort((a, b) => {
      const dateA = a.changedAt ? new Date(a.changedAt).getTime() : 0;
      const dateB = b.changedAt ? new Date(b.changedAt).getTime() : 0;
      return dateB - dateA;
    });
  }, [historyData]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (historyData.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="200px"
      >
        <Typography color="textSecondary">
          {t("keyHistory.noHistory")}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} elevation={0} sx={{ width: "100%" }}>
      <Table size="small" sx={{ width: "100%" }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>
              {t("keyHistory.columns.date")}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {t("keyHistory.columns.label")}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {t("keyHistory.columns.assignedTo")}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {t("keyHistory.columns.status")}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {t("keyHistory.columns.performedBy")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedHistoryData.map((entry, index) => {
            const changedAt = entry?.changedAt;

            const newLabel = entry?.newLabel;

            const assignedToName = entry?.assignedToUser
              ? `${entry?.assignedToUser?.firstName} ${entry?.assignedToUser?.lastName}`
              : "-";

            const statusCode = entry.newStatus;

            const performedByName = entry.changedByUser
              ? `${entry?.changedByUser?.firstName} ${entry?.changedByUser?.lastName}`
              : "-";

            return (
              <TableRow key={entry.uniqueId || index}>
                <TableCell>
                  {changedAt
                    ? utils.formatDate({ date: new Date(changedAt) })
                    : "-"}
                </TableCell>
                <TableCell>{newLabel || "-"}</TableCell>
                <TableCell>{assignedToName}</TableCell>
                <TableCell>
                  {statusCode !== undefined ? (
                    <KeyHistoryStatusBadge statusCode={statusCode} />
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell>{performedByName}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default KeyHistoryTable;
